'use client';

/**
 * Imports React and supporting libraries for the Redux store and routing.
 */
import React, { useEffect, useState } from 'react';
import './sentry-init';
import { Provider } from 'react-redux';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from 'react-router';
// const environment = import.meta.env.VITE_APP_ENV;
const growthbookApiKey = import.meta.env.VITE_GROWTHBOOK_API_KEY;

/**
 * Imports type definitions and styles, as well as a custom hook for permissions.
 */
import type { Route } from './+types/root';
import stylesheet from './app.scss?url';
import { store } from './redux/store';
import { usePayWallOnInactive, usePermitted } from '~/utils/hooks';
import { OverlayLoader } from '~/components/ui-kit';
import '~/utils/i18n';
import { GrowthBook } from '@growthbook/growthbook';
import { autoAttributesPlugin } from '@growthbook/growthbook/plugins';

/**
 * Provides the stylesheet links for the application.
 * @returns {Array<{ rel: string, href: string }>} Array of stylesheet link objects.
 */
export const links: Route.LinksFunction = () => [
  { rel: 'stylesheet', href: stylesheet },
];

/**
 * The main layout component that sets up the HTML structure, Redux provider, and service worker registration.
 * @param {{ children: React.ReactNode }} props - The children elements to be rendered inside the layout.
 * @returns {JSX.Element} The layout component with HTML structure.
 */
export function Layout({ children }: { children: React.ReactNode }) {
  /**
   * Registers the service worker if supported by the browser.
   * Logs the scope of the registered service worker and any errors.
   */
  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          console.log(
            'Service Worker registered with scope:',
            registration.scope
          );
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
      // Check if a Service Worker is active
      if (navigator.serviceWorker.controller) {
        console.log('Page is controlled by an active Service Worker.');
      } else {
        console.log('No active Service Worker is controlling this page.');
      }
    }
  };

  if (!import.meta.env.SSR && 'serviceWorker' in navigator) {
    registerServiceWorker()
      .then((registration) => {
        console.log('Service Worker registered:', registration);

        const sw = navigator.serviceWorker.controller;

        if (!sw) {
          console.warn('No active SW to postMessage');
          return;
        }

        sw.postMessage({
          type: 'SET_FILTERS',
          urlParts: ['technical_sounds/', 'sounds', '/library_card_previews'],
        });
      })
      .catch((error) => {
        console.error('Service Worker registration failed:', error);
      });
  }

  return (
    <Provider store={store}>
      <html lang="en">
        <head>
          <meta charSet="utf-8" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, user-scalable=no, viewport-fit=cover"
          />
          <meta name="mobile-web-app-capable" content="yes" />
          <meta name="apple-touch-fullscreen" content="yes" />
          <meta name="apple-mobile-web-app-capable" content="yes" />
          <meta
            name="apple-mobile-web-app-status-bar-style"
            content="#C5D8CC"
          />
          <meta name="apple-mobile-web-app-title" content="Yoga App" />

          <link rel="icon" type="image/x-icon" href="/img/favicon.ico" />
          {/* {environment === 'develop' && (
            <link rel="manifest" href="../manifest.json"></link>
          )} */}
          <Meta />
          <Links />
        </head>
        <body>
          {children}
          <div id="modal" />
          <ScrollRestoration />
          <Scripts />
        </body>
      </html>
    </Provider>
  );
}

import { useAppDispatch } from '~/redux/hooks';
import { userApi } from '~/redux/api/routes/UserApi';
import { workoutsAPI } from '~/redux/api/routes/workoutsApi';
import { useUpdateUserProperties } from '~/utils/hooks/useAmplitude';
import { setAmplitudePlanName } from '~/utils/hooks/initializationAmlitude';
import { AuthAccessor } from '~/utils/storage';
import { AppDownloadManager } from '~/components/AppDownloadManager';

/**
 * The main application component that uses a permission hook and renders the route outlet.
 * @returns {JSX.Element} The React element rendering the Outlet.
 */
export default function App() {
  const isAllowed = usePermitted();
  const dispatch = useAppDispatch();
  const updateUserProperties = useUpdateUserProperties();

  // Create a GrowthBook instance
  const growthbook = new GrowthBook({
    apiHost: 'https://cdn.growthbook.io',
    clientKey: growthbookApiKey,
    enableDevMode: true,
    trackingCallback: (experiment, result) => {
      // This is where you would send an event to your analytics provider
      console.log('Viewed Experiment', {
        experimentId: experiment.key,
        variationId: result.key,
      });
    },
    plugins: [autoAttributesPlugin()],
  });

  useEffect(() => {
    // Only fetch if we have an access token (user claims to be logged in)
    if (AuthAccessor.getAccessToken()) {
      const userPromise = dispatch(
        userApi.endpoints.getUserDetails.initiate()
      );
      userPromise
        .unwrap()
        .then((userData) => {
          updateUserProperties({ fitness_level: userData.fitness_level });
        })
        .catch((err) => {
          console.error('Failed to load user details for analytics', err);
        });

      const planPromise = dispatch(
        workoutsAPI.endpoints.getWorkoutsHistory.initiate()
      );
      planPromise
        .unwrap()
        .then((historyData) => {
          const plans = historyData.workout_plans ?? [];
          const activePlan =
            plans.find((plan) => !plan.is_completed) ?? plans.at(-1);
          if (!activePlan) return;
          setAmplitudePlanName(activePlan.yoga_type);
          updateUserProperties({ plan_name: activePlan.yoga_type });
        })
        .catch((err) => {
          console.error('Failed to load active plan for analytics', err);
        });

      return () => {
        userPromise.unsubscribe();
        planPromise.unsubscribe();
      };
    }
  }, [dispatch, updateUserProperties]);

  useEffect(() => {
    growthbook.init({ streaming: true });
  }, [growthbook]);

  const [ClientProvider, setClientProvider] =
    useState<null | React.ComponentType<{
      growthbook: GrowthBook;
      children: React.ReactNode;
    }>>(null);

  useEffect(() => {
    import('./components/GrowthbookClientProvider').then((m) =>
      setClientProvider(() => m.GrowthbookClientProvider)
    );
  }, []);

  /**
   * Check every 12h or when page will be active on screen.
   */
  usePayWallOnInactive();

  if (isAllowed === null) return <OverlayLoader />;

  if (ClientProvider) {
    return (
      <ClientProvider growthbook={growthbook}>
        <Outlet />
        <AppDownloadManager />
      </ClientProvider>
    );
  }

  // return isAllowed === null ? <OverlayLoader /> : <GrowthBookProvider growthbook={growthbook}><Outlet /></GrowthBookProvider>;
}

/**
 * The error boundary for handling route errors.
 * Displays an error message, additional details, and a stack trace if in development.
 * @param {Route.ErrorBoundaryProps} props - The error object provided by the route.
 * @returns {JSX.Element} The React element rendering the error information.
 */
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error';
    details =
      error.status === 404
        ? 'The requested page could not be found.'
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
